import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import Api from '../utils/Api';
import { useAuth } from './AuthContext';
import { useError } from './ErrorContext';
import { useLoading } from './LoadingContext';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import { useRoutePath } from '../utils/getRoutePath';

const CustomerContext = createContext();
 
export const useCustomers = () => {
  const context = useContext(CustomerContext);
  if (!context) {
    throw new Error('useCustomers must be used within a CustomerProvider');
  }
  return context;
};

export const CustomerProvider = ({ children }) => {
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { hideLoading } = useLoading();
  const { getToken } = useAuth();
  const [ setApi] = useState(null);
  const { handleApiError, formErrors, clearErrors } = useError();
  const [customers, setCustomers] = useState([]);
  const segments = location.pathname.split('/');
  const id = segments.pop();
  const [paginate, setPaginate] = useState(true);
  const getRoutePath = useRoutePath();
  const initialized = location.pathname === getRoutePath('/customers');

  const getCustomers = useCallback(async (page = 1, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
   
    try {
      const searchParams = new URLSearchParams(window.location.search);
      let query = '';
      if(filters?.q){
        query = '&q='+filters?.q;
        
      } else{
        const q = searchParams.get('q') || '';
        query = '&q='+q;
      }
      if(filters?.active){
        query += '&active='+filters?.active;
      } else{
        const active = searchParams.get('active') || '';
        query += '&active='+active;
      }
      let response = await apiInstance.call('customers?page=' + page+'&paginate='+paginate +''+query , 'GET');
      if(filters?.all){
        query = '';
        setPaginate(false)
        response = await apiInstance.call('customers');  
      }
      
      if (response.status === 200) {
        setPaginate(response.data.paginate)
        setCustomers(response.data.customers || []);
       
        
        if(filters){
          return response.data.customers;
        }
      } else {
        
        handleApiError(response.error, {
          showAlert: false,
          redirectOn401: true,
          customMessage: 'Failed to fetch customers information'
        });
      }
    } catch (error) {
     
    } finally {
      
    }
  }, [handleApiError, paginate, getToken]);

  const getCustomerByIdApi = useCallback(async (id, filters = {}) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call(`customers/${id}`, 'GET', null, true, false);
   
    if (response.status === 200) {
      
      let customers = {'data':[response.data.customer]}
      setCustomers(customers || []);
      
    } 
  }, [getToken]);

  useEffect(() => {
    
    const handleLoad = async() => {
      const currentPath = window.location.pathname;
      
      if(currentPath.includes(getRoutePath('/customers/edit/'+id))){
        if (getToken()) {
        
          
          await getCustomerByIdApi(id);
        }
      }
    };

    // Add onload listener
    window.addEventListener('load', handleLoad);

    // Cleanup listener
    return () => {
      window.removeEventListener('load', handleLoad);
    };
  }, [getRoutePath, id, getToken, getCustomerByIdApi, searchParams,setApi]);

  // Initialize API and load initial data
  useEffect(() => {
    const initializeApi = async () => {
      try {
     
        if (getToken()) {
          let page = parseInt(searchParams.get('page')) || 1;
         
          await getCustomers(page);
        }
      } catch (error) {
        hideLoading();
      }
    };
   
    if(initialized){
      initializeApi();
    }
  }, [getToken, getCustomers, searchParams, initialized,hideLoading,setApi]);

  const getCustomerAdditionalData = useCallback(async () => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }
    const response = await apiInstance.call('get-customers-data', 'GET', null, true);
    if(response.status === 200){
       return response.data;
    } else{
      return handleApiError(response.error, {
        showAlert: false,
        redirectOn401: true,
        customMessage: 'Failed to get customer data'
      });
    } 
  }, [getToken, handleApiError]);

  const addCustomer = async (data) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {

      let formData = new FormData();

      // Append documents (files)
      if (data.documents) {
        data.documents.forEach((item, index) => {
          formData.append(`documents[${index}][id]`, item.id);
          formData.append(`documents[${index}][file]`, item.file);
          formData.append(`documents[${index}][name]`, item.name);
          formData.append(`documents[${index}][size]`, item.size);
          formData.append(`documents[${index}][type]`, item.type);
          formData.append(`documents[${index}][created_at]`, item.created_at);
         
        });
      }
      
      // Remove documents from the original object so we can serialize the rest
      const dataCopy = { ...data };
      delete dataCopy.documents;
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));

      const response = await apiInstance.call('customers', 'POST', formData, true,true);
      
      if (response.status === 201 || response.status === 200) {
        // Refresh the customers list
        await getCustomers();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
            errors: response.error.validationErrors
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to add Customer'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to create customer'
      });
      return { status: 500, error: error.message };
    }
  };

  const getCustomer = (id) => {
    return customers?.data?.find(customer => Number(customer.id) === Number(id));
  };

  const getCustomerById = useCallback(async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`customers/${id}`, 'GET', null, true);
      
      if (response.status === 200) {
        return { status: response.status, data: response.data.customer };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to fetch customer details'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to fetch customer details'
      });
      return { status: 500, error: error.message };
    }
  }, [getToken, handleApiError]);

  const updateCustomer = async (id, updatedData) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      let formData = new FormData();

      // Append documents (files)
      if (updatedData.documents) {
        updatedData.documents.forEach((item, index) => {
          formData.append(`documents[${index}][id]`, item.id);
          formData.append(`documents[${index}][file]`, item.file);
          formData.append(`documents[${index}][name]`, item.name);
          formData.append(`documents[${index}][size]`, item.size);
          formData.append(`documents[${index}][type]`, item.type);
          formData.append(`documents[${index}][created_at]`, item.created_at);
         
        });
      }
      
      // Remove documents from the original object so we can serialize the rest
      const dataCopy = { ...updatedData };
      delete dataCopy.documents;
      
      // Append the rest of the data as JSON
      formData.append('data', JSON.stringify(dataCopy));
      
      // Now send the request with formData only once
      const response = await apiInstance.call(`customers/${id}`, 'POST', formData, true, true);
      
      if (response.status === 200) {
        // Refresh the customers list
        await getCustomers();
        return { status: response.status, data: response.data };
      } else {
        if (response.error.status === 422) {
          return {
            status: response.error.status,
             errors: response.error.validationErrors,
          };
        } else {
          // Only call handleApiError for non-validation errors
          handleApiError(response.error, {
            showAlert: false,
            redirectOn401: true,
            customMessage: 'Failed to update Customer'
          });
          return {
            status: response.error.status,
            error: response.error
          };
        }
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to update customer'
      });
      return { status: 500, error: error.message };
    }
  };

  const deleteCustomer = async (id) => {
    const apiInstance = Api(() => getToken());
    if (!apiInstance) {
      return;
    }

    try {
      const response = await apiInstance.call(`customers/${id}`, 'DELETE', null, true);
      
      if (response.status === 200) {
        // Refresh the customers list
        await getCustomers();
        navigate(getRoutePath('/customers'));
        return { status: response.status, data: response.data };
      } else {
        handleApiError(response.error, {
          showAlert: true,
          redirectOn401: true,
          customMessage: 'Failed to delete customer'
        });
        return { status: response.status, error: response.error };
      }
    } catch (error) {
      handleApiError(error, {
        showAlert: true,
        redirectOn401: true,
        customMessage: 'Failed to delete customer'
      });
      return { status: 500, error: error.message };
    }
  };

  return (
    <CustomerContext.Provider
      value={{
        customers,
        addCustomer,
        getCustomer,
        getCustomerById,
        updateCustomer,
        deleteCustomer,
        getCustomers,
        getCustomerAdditionalData,
        formErrors,
        clearErrors
      }}
    >
      {children}
    </CustomerContext.Provider>
  );
};

export default CustomerContext; 